// The public demo API (app-backend-demo): the one module that knows it exists. It reads
// REACT_APP_DEMO_API_URL; when that is unset nothing here runs and the app behaves exactly as it
// does without a demo. The demo API is public (no auth header) and serves one country,
// DEMO_API_COUNTRY, as a synchronous answer, so the async job flow is shimmed here: the rows are
// fetched at once, kept under a `demo-N` job id and handed back as a finished job.
//
// The shared services (activitiesApi, odmApi, mapsApi) carry one guarded line each that calls in.
// The shim never sends start/end dates: the demo API returns its whole window, so the real
// delivery dates picked on the availability picker can never ask it for days it does not have.
import { COUNTRIES, DEMO_API_COUNTRY } from '../config/countries';
import type { CountryCode, MapType } from '../config/countries';
import { DEMO_API_PRODUCTS } from '../config/products';
import type { DataProductId } from '../config/products';
import type { JobOutcome } from './dataJobs';
import { getPublicJson } from './http';
import { normalizeActivityRow } from './activitiesApi';
import type { ActivitiesJobParams, ActivityRow } from './activitiesApi';
import { normalizeODMResult } from './odmApi';
import type { ODMJobParams, ODMResult } from './odmApi';
import type { AvailableMapsResponse, BoundingBox, MapsApiResponse } from './mapsApi';

export const DEMO_API_URL = process.env.REACT_APP_DEMO_API_URL || '';
export const IS_DEMO = DEMO_API_URL !== '';

/** True when requests for this country go to the demo API instead of the data API. */
export function isDemoRouted(c: CountryCode | null | undefined): boolean {
  return IS_DEMO && c === DEMO_API_COUNTRY;
}

/** True when the demo API serves product `p` for country `c` in this build. */
export function demoServes(c: CountryCode | null | undefined, p: DataProductId): boolean {
  return isDemoRouted(c) && DEMO_API_PRODUCTS.includes(p);
}

export const isDemoJobId = (id: string | null | undefined): boolean => !!id && id.startsWith('demo-');

/** Where the area picker opens in a demo build (the demo country's registry default view). */
export const DEMO_START_VIEW = COUNTRIES[DEMO_API_COUNTRY].defaultView;

/** The base the two stand-alone demo dashboards use; keeps a local default for development. */
const DASHBOARD_BASE = DEMO_API_URL || 'http://localhost:3100';

// Raw rows by demo job id, for the demo dashboards (they want the columns the API sent).
const rawRows = new Map<string, unknown[]>();
let seq = 0;

/** The rows the demo API sent for a job, exactly as sent; [] for an unknown id. */
export function getDemoRawRows(jobId: string): unknown[] {
  return rawRows.get(jobId) ?? [];
}

function remember(prefix: string, rows: unknown[]): string {
  const jobId = `${prefix}${++seq}`;
  rawRows.set(jobId, rows);
  return jobId;
}

export async function runDemoActivitiesJob(params: ActivitiesJobParams): Promise<JobOutcome<ActivityRow>> {
  const data = await getPublicJson<{ results?: unknown[] }>(DEMO_API_URL, '/api/demo/activities', {
    query: { geo_ids: params.geo_ids.join(','), activity_type: params.activity_type },
  });
  const results = Array.isArray(data?.results) ? data.results : [];
  const jobId = remember('demo-', results);
  const rows: ActivityRow[] = [];
  for (const raw of results) {
    const row = normalizeActivityRow(raw, params.measure);
    if (row !== null) rows.push(row);
  }
  return { kind: 'rows', jobId, rows };
}

export async function runDemoOdmJob(params: ODMJobParams): Promise<JobOutcome<ODMResult>> {
  // The demo API is anchored on one set of cells. "Anywhere" is an empty list on the other side
  // (see ANYWHERE in utils/odmDirection.ts): origins named means outflows from them, only
  // destinations named means inflows to them.
  const inflows = params.origin_geo_ids.length === 0 && params.destination_geo_ids.length > 0;
  const anchor = inflows ? params.destination_geo_ids : params.origin_geo_ids;
  const data = await getPublicJson<{ rows?: Array<Record<string, unknown>> }>(DEMO_API_URL, '/api/demo/odm', {
    query: {
      origin_ids: anchor.join(','),
      ...(inflows ? { direction: 'destination' } : {}),
      min_trips: 10,
      limit: 1500,
    },
  });
  const all = Array.isArray(data?.rows) ? data.rows : [];
  // Both ends named: only the trips that also end in the named cells.
  const ends = !inflows && params.destination_geo_ids.length > 0 ? new Set(params.destination_geo_ids.map(String)) : null;
  const raw = ends ? all.filter((r) => ends.has(String(r.dest_cell))) : all;
  const jobId = remember('demo-odm-', raw);
  const rows = raw.map((r) =>
    normalizeODMResult({
      batch_date: '',
      time_bucket: null,
      origin_geo_id: r.origin_cell,
      destination_geo_id: r.dest_cell,
      trips: r.trips,
      General_Distance: (Number(r.dist_km) || 0) * 1000,
      Average_Speed: null,
    }),
  );
  return { kind: 'rows', jobId, rows };
}

// ── Maps (same shapes as the data API's /maps) ────────────────────────────────────────────────

export function getDemoMapData(type: MapType, bbox: BoundingBox, countryCode: CountryCode): Promise<MapsApiResponse> {
  const { lng1, lat1, lng2, lat2 } = bbox;
  return getPublicJson<MapsApiResponse>(DEMO_API_URL, '/api/demo/maps', {
    query: { geo_type: type, lng1, lat1, lng2, lat2, country_code: countryCode },
  });
}

export function getDemoGeosByIds(type: MapType, geoIds: string[], countryCode: CountryCode): Promise<MapsApiResponse> {
  return getPublicJson<MapsApiResponse>(DEMO_API_URL, '/api/demo/maps/by-ids', {
    query: { geo_type: type, geo_ids: geoIds.join(','), country_code: countryCode },
  });
}

export function getDemoAvailableMaps(countryCode: CountryCode): Promise<AvailableMapsResponse> {
  return getPublicJson<AvailableMapsResponse>(DEMO_API_URL, '/api/demo/maps/list', {
    query: { country_code: countryCode },
  });
}

// ── Stand-alone demo dashboards ───────────────────────────────────────────────────────────────

/** OdmDashboard: the trips to or from the selected cells (arcs, hourly and daily totals). */
export function fetchDemoOdmAnchored<T>(query: { origin_ids: string; direction: string; limit: number }): Promise<T> {
  return getPublicJson<T>(DASHBOARD_BASE, '/api/demo/odm', { query });
}

/** NationalOdmDemo: the national flow table. */
export function fetchDemoNationalOdm<T>(): Promise<T> {
  return getPublicJson<T>(DASHBOARD_BASE, '/api/demo/odm', { query: { limit: 4000, min_trips: 10 } });
}
